import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AuthFrame from "./AuthFrame";
import { TitleBlock, Field, Input, ErrorNote, WarnNote, Actions, PrimaryBtn, SecondaryBtn } from "./pieces";
import chevronDown from "../../../assets/brand/onboarding/chevron-down.svg";
import { readSignup } from "./signupState";
import { registerFromSignup } from "./signupFlow";

// The date input gives "YYYY-MM-DD". new Date() would read that as UTC
// midnight, which is the day before in every US time zone, so the age is
// taken from the date parts against today's local date instead.
export const ageOn = (iso, today = new Date()) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const check = new Date(y, mo - 1, d);
  if (check.getFullYear() !== y || check.getMonth() !== mo - 1 || check.getDate() !== d) return null;
  let age = today.getFullYear() - y;
  const month = today.getMonth() + 1;
  if (month < mo || (month === mo && today.getDate() < d)) age -= 1;
  return age;
};

// Today as "YYYY-MM-DD" in local time, for the date input's max.
export const localToday = (today = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
};

// 05 · Date of birth and guardian. Creates the account (verified by the
// code ticket) and starts the session, then hands over to onboarding.
const SignupBirthdayPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Read once, when the page opens. Creating the account clears the state;
  // re-reading it on every render made the guard below fire on that render
  // and replace /onboarding with the email step.
  const [saved] = useState(readSignup);
  const [dob, setDob] = useState(saved.dob || "");
  const [guardian, setGuardian] = useState(saved.guardianEmail || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!saved.ticket || !saved.firstName) navigate("/signup/email", { replace: true });
  }, [saved.ticket, saved.firstName, navigate]);

  const age = useMemo(() => ageOn(dob), [dob]);
  const minor = age != null && age < 18;
  const tooYoung = age != null && age < 13;

  const create = async (withGuardian) => {
    if (!dob || age == null) { setError("Enter your birthday."); return; }
    if (tooYoung) { setError("You need to be at least 13 to use CACHE."); return; }
    const guardianEmail = withGuardian ? guardian.trim().toLowerCase() : "";
    if (withGuardian && guardianEmail && !/\S+@\S+\.\S+/.test(guardianEmail)) { setError("Enter a valid guardian email."); return; }
    if (withGuardian && guardianEmail && guardianEmail === (saved.email || "").toLowerCase()) { setError("The guardian email must be different from your own."); return; }
    setBusy(true); setError("");
    try {
      const problem = await registerFromSignup({ identity: saved, roleKey: "IS_ATHLETE", extra: { dateOfBirth: dob, guardianEmail }, dispatch, navigate });
      if (problem?.nameProblem) {
        // A name the server refuses is fixed on the name screen, not here.
        toast.error(problem.message);
        navigate("/signup/name");
      } else if (problem) {
        setError(problem.message);
      }
    } catch {
      setError("We could not reach CACHE. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <AuthFrame progress={{ step: 1, total: 4 }} onBack={() => navigate("/signup/name")}>
      <TitleBlock eyebrow="Required" title="What's your birthday?" sub="Athletes under 18 need a parent or guardian to co-sign any deal. Your profile works either way." />
      {/* iOS Safari centres a date input's value; on phones it starts at
          the left, as Figma 1579:934 draws it. */}
      <span className="relative block">
        <Input type="date" className="max-sm:[&::-webkit-date-and-time-value]:text-left" value={dob} max={localToday()} onChange={(e) => setDob(e.target.value)} aria-label="Date of birth" />
        <img src={chevronDown} alt="" className="pointer-events-none absolute right-[18px] top-1/2 h-2 w-[14px] -translate-y-1/2" />
      </span>
      {age != null && (
        <WarnNote badge={`You are ${age}`}>
          {minor
            ? "We will ask for a guardian to co-sign before your first contract. You can add them now or later from My Circle."
            : "You can sign your own deals. You can still add a parent or guardian to My Circle later."}
        </WarnNote>
      )}
      <Field label="Guardian email · optional now">
        <Input type="email" placeholder="parent@email.com" value={guardian} onChange={(e) => setGuardian(e.target.value)} />
      </Field>
      {error && <ErrorNote error={error} onLogin={() => navigate("/login")} />}
      <Actions>
        <PrimaryBtn onClick={() => create(true)} loading={busy} disabled={!dob}>Continue</PrimaryBtn>
        <SecondaryBtn onClick={() => create(false)} disabled={busy || !dob}>Add my guardian later</SecondaryBtn>
      </Actions>
    </AuthFrame>
  );
};

export default SignupBirthdayPage;
